import { Component } from '@theme/component';
import { debounce, isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    unlockScroll(this.refs.dialog);
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Quick-add uses a native <dialog> top-layer. On a few Chromium/GPU
    // combinations, animating that top-layer out can leave composited pieces
    // of the product card painted after the dialog is already closing. Reset
    // the emergency close styles before every new open.
    if (dialog.classList.contains('quick-add-modal') && dialog.dataset.wemondForceClosed === 'true') {
      delete dialog.dataset.wemondForceClosed;
      dialog.style.removeProperty('animation');
      dialog.style.removeProperty('transition');
      dialog.style.removeProperty('opacity');
      dialog.style.removeProperty('visibility');
      dialog.style.removeProperty('pointer-events');
      dialog.style.removeProperty('transform');
    }

    this.#previousScrollY = getScrollTop();

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      lockScroll(dialog);

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;
    if (dialog.classList.contains('dialog-closing') && !dialog.classList.contains('quick-add-modal')) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Wemond quick-add: remove it from the browser top-layer synchronously.
    // The normal transform/opacity close animation is intentionally skipped
    // only for this dialog because it is the source of persistent GPU ghost
    // tiles on some Chromium builds. Canceling descendant animations first
    // also prevents media/variant layers from surviving as compositor tiles.
    if (dialog.classList.contains('quick-add-modal')) {
      try {
        dialog.getAnimations({ subtree: true }).forEach((animation) => animation.cancel());
      } catch (_) {}

      dialog.classList.remove('dialog-closing');
      dialog.dataset.wemondForceClosed = 'true';
      dialog.style.animation = 'none';
      dialog.style.transition = 'none';
      dialog.style.opacity = '0';
      dialog.style.visibility = 'hidden';
      dialog.style.pointerEvents = 'none';
      dialog.style.transform = 'none';

      // Flush the hidden paint state before leaving the native top-layer.
      void dialog.offsetWidth;

      unlockScroll(dialog);
      scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
      if (dialog.open) dialog.close();

      // Discard the heavy morphed product subtree after the dialog is gone.
      // It is rebuilt from the cached product card on the next quick-add open.
      const modalContent = dialog.querySelector('#quick-add-modal-content');
      modalContent?.replaceChildren();

      this.dispatchEvent(new DialogCloseEvent());
      return;
    }

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    // Some Chromium builds keep a finished backdrop/composited child animation
    // in the dialog animation list. In that case `animation.finished` never
    // settles and the native dialog remains open (and keeps scroll locked).
    // Preserve the normal close animation, but never let a stale animation
    // hold the interface longer than the visual transition itself.
    await Promise.race([
      onAnimationEnd(dialog, undefined, { subtree: false }),
      new Promise((resolve) => window.setTimeout(resolve, 300)),
    ]);

    unlockScroll(dialog);
    scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    if (dialog.open) dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          lockScroll(event.target);
        } else {
          unlockScroll(event.target);
        }
      }
    }
  },
  { capture: true }
);
